// src/pages/Contact.jsx

import React, { useState, useEffect } from "react";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";

import { submitInquiry, getSettings } from "../api";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    service: "",
  });

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getSettings().then(setSettings);
  }, []);

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);

    try {
      const res = await submitInquiry(form);

      if (res && (res.status === "success" || res.ok)) {
        toast.success("Inquiry Submitted", {
          description: "Our team will contact you shortly.",
        });

        setForm({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
          service: "",
        });
      } else {
        toast.error("Submission failed");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-[#fafafa]">
      <PageHero
        title="Contact Us"
        subtitle="Connect with SSC READYMIX for inquiries, project discussions, concrete solutions, and technical support."
        num="06"
      />

      <section className="py-20 md:py-28 relative overflow-hidden">
        {/* Background Glow */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#ef2435]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#ef2435]/5 rounded-full blur-3xl" />

        <div className="max-w-[1500px] mx-auto px-5 md:px-8 relative">

          {/* TOP GRID */}
          <div className="grid lg:grid-cols-12 gap-8">

            {/* LEFT INFO */}
            <div className="lg:col-span-4 space-y-6">

              {/* CONTACT CARD */}
              <div className="bg-white rounded-[32px] border border-[#ececef] p-8 shadow-sm">

                <div className="inline-flex items-center gap-2 mb-8">
                  <div className="w-10 h-10 rounded-2xl bg-[#ef2435]/10 flex items-center justify-center">
                    <Phone className="text-[#ef2435]" size={18} />
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#ef2435] font-semibold">
                      Contact Information
                    </p>

                    <h3 className="text-2xl font-black text-gray-900">
                      Reach Out
                    </h3>
                  </div>
                </div>

                <div className="space-y-6">

                  {/* ADDRESS */}
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-[#fafafa] border border-[#ececef] flex items-center justify-center shrink-0">
                      <MapPin size={18} className="text-[#ef2435]" />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-semibold">
                        Company Address
                      </p>

                     <div
  className="text-gray-700 leading-relaxed"
  dangerouslySetInnerHTML={{
    __html:
      settings.address_hq ||
      "SSC READYMIX Pvt. Ltd., Odisha, India",
  }}
/>
                    </div>
                  </div>

                  {/* MOBILE */}
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-[#fafafa] border border-[#ececef] flex items-center justify-center shrink-0">
                      <Phone size={18} className="text-[#ef2435]" />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-semibold">
                        Mobile Number
                      </p>

                      <a
                        href={`tel:${settings.phone || "+919861934840"}`}
                        className="text-gray-900 font-semibold hover:text-[#ef2435]"
                      >
                        {settings.phone || "+91 98619 34840"}
                      </a>
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-[#fafafa] border border-[#ececef] flex items-center justify-center shrink-0">
                      <Mail size={18} className="text-[#ef2435]" />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-semibold">
                        Email Address
                      </p>

                      <a
                        href={`mailto:${settings.email || "info@sscreadymix.com"}`}
                        className="text-gray-900 font-semibold hover:text-[#ef2435]"
                      >
                        {settings.email || "info@sscreadymix.com"}
                      </a>
                    </div>
                  </div>

                  {/* HOURS */}
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-[#fafafa] border border-[#ececef] flex items-center justify-center shrink-0">
                      <Clock size={18} className="text-[#ef2435]" />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-semibold">
                        Working Hours
                      </p>

                      <p className="text-gray-700">
                        Mon - Sat · 9:00 AM - 7:00 PM
                      </p>
                    </div>
                  </div>
                </div>

                {/* SOCIAL LINKS */}
                <div className="mt-10 pt-8 border-t border-[#ececef]">

                  <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5 font-semibold">
                    Social Media
                  </p>

                  <div className="flex gap-3">

                    {[
                      {
                        icon: Facebook,
                        link: settings.facebook || "#",
                      },
                      {
                        icon: Instagram,
                        link: settings.instagram || "#",
                      },
                      {
                        icon: Linkedin,
                        link: settings.linkedin || "#",
                      },
                    ].map((s, i) => (
                      <a
                        key={i}
                        href={s.link}
                        target="_blank"
                        rel="noreferrer"
                        className="w-12 h-12 rounded-2xl bg-[#fafafa] border border-[#ececef] flex items-center justify-center hover:bg-[#ef2435] hover:border-[#ef2435] group transition-all duration-300"
                      >
                        <s.icon
                          size={18}
                          className="text-gray-700 group-hover:text-white"
                        />
                      </a>
                    ))}

                  </div>
                </div>
              </div>

              {/* WHATSAPP BUTTON */}
              <a
                href={`https://wa.me/${(settings.phone || "919861934840").replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-[30px] p-6 flex items-center justify-between transition-all duration-300 shadow-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                    <MessageCircle size={28} />
                  </div>

                  <div>
                    <p className="text-sm opacity-90">
                      Quick Support
                    </p>

                    <h3 className="text-2xl font-black">
                      WhatsApp Us
                    </h3>
                  </div>
                </div>

                <ArrowUpRight />
              </a>
            </div>

            {/* FORM */}
            <div className="lg:col-span-8">
              <form
                onSubmit={submit}
                className="bg-white rounded-[36px] border border-[#ececef] p-8 md:p-12 shadow-sm"
              >

                <div className="mb-10">

                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ef2435]/10 text-[#ef2435] text-[11px] uppercase tracking-[0.25em] font-semibold mb-6">
                    Inquiry Form
                  </div>

                  <h2 className="text-4xl md:text-6xl font-black text-gray-900 leading-tight">
                    Tell Us About
                    <br />
                    Your Project
                  </h2>
                </div>

                {/* FORM GRID */}
                <div className="grid md:grid-cols-2 gap-6">

                  <FormField label="Full Name" required>
                    <Input
                      required
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                      className="contact-input"
                    />
                  </FormField>

                  <FormField label="Company Name">
                    <Input
                      value={form.company}
                      onChange={(e) =>
                        setForm({ ...form, company: e.target.value })
                      }
                      className="contact-input"
                    />
                  </FormField>

                  <FormField label="Email Address" required>
                    <Input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                      className="contact-input"
                    />
                  </FormField>

                  <FormField label="Mobile Number" required>
                    <Input
                      required
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      className="contact-input"
                    />
                  </FormField>
                </div>

                {/* SERVICE */}
                <div className="mt-8">
                  <Label className="contact-label">
                    Service Interest
                  </Label>

                  <div className="flex flex-wrap gap-3 mt-4">

                    {[
                      "Standard Mix",
                      "Custom Mix",
                      "Bulk Supply",
                      "Infrastructure",
                      "Commercial",
                      "Residential",
                    ].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() =>
                          setForm({ ...form, service: s })
                        }
                        className={`px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                          form.service === s
                            ? "bg-[#ef2435] text-white"
                            : "bg-[#fafafa] border border-[#ececef] text-gray-700 hover:border-[#ef2435] hover:text-[#ef2435]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}

                  </div>
                </div>

                {/* MESSAGE */}
                <FormField label="Project Details" required>
                  <Textarea
                    rows={6}
                    required
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    className="contact-input resize-none pt-4"
                    placeholder="Share your project requirements, quantity, location, and timeline..."
                  />
                </FormField>

                {/* BUTTON */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="mt-8 h-14 px-8 rounded-full bg-[#ef2435] hover:bg-[#d91f30] text-white text-sm font-semibold"
                >
                  {loading ? "Sending..." : "Submit Inquiry"}

                  <ArrowUpRight size={18} className="ml-2" />
                </Button>
              </form>
            </div>
          </div>

          
          {/* GOOGLE MAP */}
<div className="rounded-[32px] overflow-hidden border border-[#ececef] bg-white shadow-sm">
  <div className="p-6 border-b border-[#ececef] flex items-center justify-between">
    <div>
      <p className="text-[11px] tracking-[0.3em] uppercase text-[#ef2435] font-semibold mb-2">
        Location Map
      </p>

      <h3 className="text-2xl font-black text-gray-900">
        Visit Our Office
      </h3>
    </div>

    <a
      href="https://maps.app.goo.gl/wnZ3Gx1grHfTy6rF9"
      target="_blank"
      rel="noreferrer"
      className="bg-[#ef2435] hover:bg-[#d81f2f] text-white px-5 py-3 rounded-full text-sm font-semibold transition-all"
    >
      Open Map
    </a>
  </div>

  <div className="h-[420px] w-full">
    <iframe
      title="SSC Readymix Location"
      src="https://maps.google.com/maps?q=SSC%20READYMIX&t=&z=13&ie=UTF8&iwloc=&output=embed"
      className="w-full h-full border-0"
      loading="lazy"
      allowFullScreen
    />
  </div>
</div>
        </div>
      </section>

      <style>{`
        .contact-input {
          height: 52px !important;
          border-radius: 18px !important;
          border: 1px solid #ececef !important;
          background: #fafafa !important;
          color: #111 !important;
          font-size: 15px !important;
        }

        .contact-input:focus {
          border-color: #ef2435 !important;
          box-shadow: 0 0 0 3px rgba(239,36,53,0.08) !important;
        }

        .contact-label {
          display: block;
          margin-bottom: 10px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.25em;
          color: #6b7280;
          font-weight: 700;
        }
      `}</style>
    </main>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="mt-6 first:mt-0">
      <Label className="contact-label">
        {label}
        {required && (
          <span className="text-[#ef2435] ml-1">*</span>
        )}
      </Label>

      {children}
    </div>
  );
}